import { useMemo } from "react"
import { If, Else } from "lib/@components/switch"
import { FREQUENCY_LABELS, FREQUENCY_ORDER, FacilityDrillDownContent } from "./facility-drill-down-content"
import { FacilityDrillDownServerContent } from "./facility-drill-down-server-content"

export function FacilityDrillDownDialog({ facility, costResult, currencySymbol, detailArgs, cancel }) {
    // Database-backed results have no inline task array; the server aggregates the
    // facility's own row range instead.
    const serverMode = !!costResult?.detailsRef?.setKey && !!detailArgs
    const facilityTasks = useMemo(getFacilityTasks, [costResult, facility, serverMode])
    const criticalityBreakdown = useMemo(buildCriticalityBreakdown, [facilityTasks])
    const frequencyBreakdown = useMemo(buildFrequencyBreakdown, [facilityTasks])

    return (
        <If value={serverMode}>
            <FacilityDrillDownServerContent
                facility={facility}
                currencySymbol={currencySymbol}
                detailArgs={detailArgs}
                onClose={handleClose}
            />
            <Else>
                <FacilityDrillDownContent
                    facility={facility}
                    currencySymbol={currencySymbol}
                    taskCount={facilityTasks.length}
                    criticalityBreakdown={criticalityBreakdown}
                    frequencyBreakdown={frequencyBreakdown}
                    loading={false}
                    onClose={handleClose}
                />
            </Else>
        </If>
    )

    function getFacilityTasks() {
        if (serverMode) return []
        const tasks = costResult?.taskBreakdown ?? []
        return tasks.filter(matchesFacilityName(facility?.facilityName))
    }

    function buildCriticalityBreakdown() {
        const map = {}
        for (const task of facilityTasks) {
            const key = task.classification || "Unclassified"
            if (!map[key]) map[key] = { label: key, cost: 0, hours: 0, count: 0 }
            map[key].cost += task.annualCost || 0
            map[key].hours += task.annualHours || 0
            map[key].count += 1
        }
        return Object.values(map).sort(sortByCostDescending)
    }

    function buildFrequencyBreakdown() {
        const map = {}
        for (const task of facilityTasks) {
            const key = task.frequencyBucket || "other"
            const label = FREQUENCY_LABELS[key] || key
            if (!map[key]) map[key] = { key, label, cost: 0, hours: 0, count: 0 }
            map[key].cost += task.annualCost || 0
            map[key].hours += task.annualHours || 0
            map[key].count += 1
        }
        return FREQUENCY_ORDER.filter(hasEntry).map(toEntry)

        function hasEntry(k) {
            return !!map[k]
        }
        function toEntry(k) {
            return map[k]
        }
    }

    function sortByCostDescending(a, b) {
        return b.cost - a.cost
    }

    function handleClose() {
        cancel()
    }
}

function matchesFacilityName(facilityName) {
    return function isFacilityTask(task) {
        return task.facilityName === facilityName
    }
}
